import { dismiss, getQueue, mutate, type QueuedMutation } from './api'
import { visibleTime } from './business-settings'
import { clockOf, minutesOfDay, strictMinutes } from './day-order'
import { visitJobLabel } from './job-names'
import { askParking, type ParkingCustomer } from './parking-ask'
import { pinFoot } from './pin-foot'
import { agreedTimeKey, timeIsAgreed } from './rebook-message'
import { screenLocale, shownTime } from './round-config'
import { routeGen } from './route-state'
import { timeWheel } from './time-wheel'
import { blockedWords, el } from './ui'
import { undoPatch } from './undo'
import type { Clean } from './types'

// The time of a visit, shared by the Diary and Today (Liam, 4 Nov 2026, design H: tap a time on Today opens the same
// sheet, https://claude.ai/artifact/74zWf8kUcfCMSk2dJpPpKE). Moved here from schedule.ts unchanged; each screen passes
// its own redraw: `changed` once the server answers (and after Undo), `drawNow` to show the waiting change at once.
export interface VisitTimeHooks {
  changed: () => void
  drawNow?: () => void
  // Today's own rows under the time (design H: Message them, Whose job), given a way to close the sheet first.
  extra?: (close: () => void) => HTMLElement | null
}

type TimeVisit = Pick<Clean, 'id' | 'date' | 'version' | 'confirmed' | 'time_agreed_for' | 'customer_id' | 'kind'> &
  { time_slot?: string | null; customer_name?: string | null; customer_parking?: string | null; duration_minutes?: number | null; blocked_reason?: string | null; blocked_note?: string | null; job_id?: number | null; job_name?: string | null }

const parkingOf = (cl: TimeVisit): ParkingCustomer => ({ customer_name: cl.customer_name ?? null, parking: cl.customer_parking ?? null, duration_minutes: cl.duration_minutes ?? null })

// The time sheet (tidy-up 2 item 1; Liam, 20 Oct 2026: "Times arent tappable how do i easily change that"; drawn and
// approved https://claude.ai/artifact/QjKSqEL4tJpq2uPeLb4CA1; docs/plans/tidy-up-2/AGREED.md 1). One sheet for the phone
// and the computer, from a Day row's time, the computer's time button, the visit's details and Today's time: the times
// around it (8am to 12pm hourly with none), Other… for any time, "Time agreed with them" (only once the day is agreed,
// the booking form's rule), Save naming the time, Remove the time. The same save as the ⋯ menu's "Change the time…".
const TIME_ONLY_KEYS = new Set(['version', 'time_slot', 'confirmed', 'time_agreed_for', 'undo_restore', 'pencil'])
export const timeOnly = (q: QueuedMutation): boolean => q.method === 'PATCH' && /^\/api\/cleans\/\d+$/.test(q.path) && !!q.body && typeof q.body === 'object' &&
  Object.keys(q.body as Record<string, unknown>).every((k) => TIME_ONLY_KEYS.has(k))
// A time save of this visit the server refused (another phone changed it): what was chosen comes back picked, and it is
// dismissed when the next one is saved, so the new one is not held behind it (Sonnet's review, F16).
function refusedTimeSave(cl: TimeVisit): QueuedMutation | null {
  return getQueue().filter((q) => q.path === `/api/cleans/${cl.id}` && (q.status === 'conflict' || q.status === 'failed') && timeOnly(q) && !(q.body as { pencil?: unknown }).pencil).at(-1) ?? null
}
export function openTimePicker(cl: TimeVisit, opener: HTMLElement, hooks: VisitTimeHooks): void {
  const gen = routeGen
  const who = cl.customer_name ?? 'this visit'
  const was = visibleTime(cl)
  const wasAgreed = timeIsAgreed(cl)
  const refused = refusedTimeSave(cl)
  const refusedBody = (refused?.body ?? null) as { time_slot?: unknown; time_agreed_for?: unknown } | null
  let picked: string | null = refusedBody && (typeof refusedBody.time_slot === 'string' || refusedBody.time_slot === null) ? refusedBody.time_slot as string | null : was
  let agreed = refusedBody && 'time_agreed_for' in refusedBody ? typeof refusedBody.time_agreed_for === 'string' : wasAgreed
  const dialog = el('dialog', { class: 'booking-dialog diary-time-dialog', 'aria-label': `${was ? 'Change the time' : 'Set a time'} for ${who}` }) as HTMLDialogElement
  const close = el('button', { type: 'button', class: 'btn-secondary' }, 'Close')
  const head = el('header', { class: 'booking-head' }, el('h2', {}, `Time for ${who}`), close)
  const line = el('p', { class: 'diary-time-line muted' }, [new Date(`${cl.date}T12:00:00`).toLocaleDateString(screenLocale(), { weekday: 'long', day: 'numeric', month: 'long' }), visitJobLabel(cl)].filter(Boolean).join(' · '))
  // The times around the visit's: the half hours an hour either side; a time typed by hand ("9am-11am") is its own
  // choice, with the half hours around its start; none, the morning hours.
  const start = strictMinutes(was) ?? minutesOfDay(was)
  const around = start === null ? [480, 540, 600, 660, 720] : [-60, -30, 0, 30, 60].map((d) => start + d).filter((m) => m >= 0 && m < 1440)
  const choices: { value: string; words: string }[] = [
    ...(was && strictMinutes(was) === null ? [{ value: was, words: shownTime(was) }] : []),
    ...around.map((m) => ({ value: clockOf(m), words: shownTime(clockOf(m)) })),
  ]
  const chips = el('div', { class: 'booking-chips diary-time-chips', role: 'group', 'aria-label': 'Times' })
  const chipFor = new Map<string, HTMLButtonElement>()
  for (const c of choices) {
    const b = el('button', { type: 'button', class: 'booking-chip diary-time-chip', 'aria-pressed': 'false' }, c.words) as HTMLButtonElement
    b.addEventListener('click', () => { pick(c.value) })
    chipFor.set(c.value, b); chips.append(b)
  }
  const other = el('button', { type: 'button', class: 'booking-chip diary-time-chip', 'aria-pressed': 'false' }, 'Other…') as HTMLButtonElement
  // Other… opens Roundly's own time wheel under the chips (Liam, 27 Oct 2026: the phone's wheel "dissapears like ive
  // clicked away also its not on brand"): the box behind it stays for a keyboard and a screen reader (time-wheel.ts).
  const box = el('input', { type: 'time', class: 'diary-time-other time-chooser-input time-chooser-one', 'aria-label': `Another time for ${who}` }) as HTMLInputElement
  const otherBox = el('div', { class: 'diary-time-wheel time-chooser-wheeled' }, box)
  otherBox.hidden = true
  let wheeled = false
  chips.append(other)
  const agreedNote = el('p', { class: 'diary-time-note' }, wasAgreed && was ? `They agreed ${shownTime(was)}.` : '')
  const blocked = cl.blocked_reason ? el('p', { class: 'diary-time-note' }, `Changing the time clears Couldn't do it (${blockedWords(cl.blocked_reason, cl.blocked_note)}). Undo puts it back.`) : null
  const refusedNote = refused ? el('p', { class: 'diary-time-note' }, `That didn't save: ${refused.error ?? 'it was changed on another phone'}. Save to try again.`) : null
  const sw = el('input', { type: 'checkbox', role: 'switch', class: 'booking-switch-input' }) as HTMLInputElement
  const switchRow = el('label', { class: 'booking-switch-row diary-time-agreed' }, el('span', { class: 'booking-switch-label' }, 'Time agreed with them'), sw,
    el('span', { class: 'booking-switch', 'aria-hidden': 'true' }, el('span', { class: 'booking-switch-knob' })))
  const dayFirst = el('p', { class: 'diary-time-note muted' }, cl.confirmed === 1 ? '' : 'Agree the day first.')
  const save = el('button', { type: 'button', class: 'btn-primary diary-time-save' }, 'Save') as HTMLButtonElement
  const remove = was ? el('button', { type: 'button', class: 'link-button diary-time-remove' }, 'Remove the time') : null
  const sync = (): void => {
    for (const [value, b] of chipFor) b.setAttribute('aria-pressed', String(value === picked && otherBox.hidden))
    other.setAttribute('aria-pressed', String(!otherBox.hidden))
    sw.disabled = cl.confirmed !== 1 || !picked
    sw.checked = !sw.disabled && agreed
    const timeChanged = picked !== was
    const agreedChanged = !timeChanged && sw.checked !== wasAgreed
    save.disabled = !picked || (!timeChanged && !agreedChanged && !refused)
    save.textContent = !picked ? 'Save' : agreedChanged ? sw.checked ? 'Save: time agreed' : 'Save: time not agreed' : `Save ${shownTime(picked)}`
  }
  // A new time turns Time agreed off, as the booking form does (booking.ts); it can be turned on again before saving.
  const pick = (value: string): void => { if (value !== picked) agreed = false; picked = value; otherBox.hidden = true; sync() }
  other.addEventListener('click', () => {
    otherBox.hidden = false
    box.value = picked && /^\d{2}:\d{2}/.test(picked) ? picked.slice(0, 5) : '12:00'
    // Made when first shown (a wheel out of sight cannot be scrolled to its time); after that, put on the time.
    if (!wheeled) { wheeled = true; otherBox.append(timeWheel(box).el) } else box.dispatchEvent(new Event('change'))
    fromBox()
  })
  const fromBox = (): void => { if (box.value) { if (box.value !== picked) agreed = false; picked = box.value; sync() } }
  box.addEventListener('input', fromBox)
  box.addEventListener('change', fromBox)
  sw.addEventListener('change', () => { agreed = sw.checked; sync() })
  save.addEventListener('click', () => {
    if (save.disabled || !picked) return
    dialog.close()
    if (routeGen !== gen) return
    if (refused) dismiss(refused.key)
    const time = picked
    saveVisitTime(cl, time, time === was ? sw.checked : cl.confirmed === 1 && sw.checked, hooks)
  })
  remove?.addEventListener('click', () => {
    dialog.close()
    if (routeGen !== gen) return
    if (refused) dismiss(refused.key)
    saveVisitTime(cl, null, false, hooks)
  })
  close.addEventListener('click', () => dialog.close())
  const extra = hooks.extra?.(() => dialog.close()) ?? null
  dialog.append(head, el('div', { class: 'diary-time-body' }, line, ...(refusedNote ? [refusedNote] : []), chips, otherBox, ...(agreedNote.textContent ? [agreedNote] : []), ...(blocked ? [blocked] : []), switchRow, ...(dayFirst.textContent ? [dayFirst] : []), ...(extra ? [extra] : []), pinFoot(save, remove)))
  dialog.addEventListener('close', () => { dialog.remove(); if (opener.isConnected) opener.focus({ preventScroll: true }) })
  sync()
  document.body.append(dialog)
  dialog.showModal()
}

// A time on a visit, from the menu rather than the booking panel. The server
// clears `confirmed` on any date OR time change unless the body says
// otherwise (functions/api/cleans/[id].ts) — and putting a time on a day the
// customer already agreed must not silently un-agree it, so the current value
// is sent back with it. A date move keeps the reset: a new day is not agreed.
// The time of a visit, its agreement, or taking the time off (time null): the time sheet's save and the ⋯ menu's
// "Change the time…". `agree`: the time agreed with them (only once the day is agreed); null, as the ⋯ saves, leaves
// the server to turn a changed time's agreement off. Drawn at once, with or without signal (the screen's drawNow).
// Undo puts back the time, the agreements and Couldn't do it (undo_restore: the server reads what the change cleared
// from its own history; tidy-up 2 item 1 found the Undo without it).
export function saveVisitTime(cl: TimeVisit, time: string | null, agree: boolean | null, hooks: VisitTimeHooks, parkingOk = false): void {
  const was = { version: cl.version, time_slot: cl.time_slot ?? null, confirmed: cl.confirmed, time_agreed_for: timeIsAgreed(cl) ? cl.time_agreed_for ?? null : null }
  const who = cl.customer_name ?? 'Visit'
  const sameTime = time === (cl.time_slot ?? null)
  // A new time in their parking hours is asked about first (Parking notes, 0084).
  if (!parkingOk && time && !sameTime && askParking(parkingOf(cl), time, () => saveVisitTime(cl, time, agree, hooks, true), cl.date)) return
  const key = (t: string | null): string | null => agreedTimeKey(cl.date, t)
  const body: Record<string, unknown> = sameTime && agree !== null
    ? { version: cl.version, time_agreed_for: agree ? key(time) : null }
    : { version: cl.version, time_slot: time, confirmed: cl.confirmed, ...(agree && time && cl.confirmed === 1 ? { time_agreed_for: key(time) } : {}) }
  const text = sameTime && agree !== null
    ? agree ? `${who}: time agreed, ${shownTime(time ?? '')}` : `${who}: time not agreed`
    : time ? `${who} set to ${shownTime(time)}` : `${who}: time taken off`
  const path = `/api/cleans/${cl.id}`
  let queued = false
  const saving = mutate({
    label: time ? `Set time: ${cl.customer_name ?? ''} at ${time}` : `Remove time: ${cl.customer_name ?? ''} ${cl.date}`,
    method: 'PATCH',
    path,
    body,
    onAck: hooks.changed,
    onConflict: hooks.changed,
    onQueued: () => { queued = true },
  })
  // Drawn at once where it goes.
  hooks.drawNow?.()
  void saving.then((ack) => {
    if (!ack.ok && ack.status >= 400 && ack.status < 500) {
      // A visit gone from the round says so in plain words (Astra F07, Sol R15, Sonnet F15); anything else in the server's.
      window.alert(ack.status === 404 && ack.code !== 'customer_gone' ? 'That visit is no longer in the diary.' : ack.error ?? 'That did not save.')
      hooks.changed()
    }
    undoPatch({
      text: `${text}${queued || ack.status === 0 ? ', waiting for signal' : ''}`,
      label: `Undo time: ${cl.customer_name ?? ''} ${cl.date}`,
      path,
      ack,
      version: was.version,
      body: sameTime && agree !== null ? { time_agreed_for: was.time_agreed_for } : { time_slot: was.time_slot, confirmed: was.confirmed, time_agreed_for: was.time_agreed_for, undo_restore: true },
      after: hooks.changed,
    })
  })
}
